import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useStoreState, useStoreActions } from '../../../store';
import { createUseStyles } from 'react-jss';
import {
  defaultAssetTableSortComparator,
  fomattedScheduleAsList,
  formatAddressAsList,
  formattedContactAsList,
  isCountryAllowed,
  renderHydrationValidationStatus,
} from '../utils';
import { SearchBar } from '../../shared/SearchBar';
import type { OriginType } from '../utils';
import { OriginTypeDisplayMap } from '../utils';
import type { HydratedOrigin } from '../../../store/fulfillmentContext/types';
import { escapeRegexChars } from '../../../utils/regex';
import type { PartnerOrigin } from '@shipium-corp/shipium-ts-types';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
  },
});

export interface OriginSelectTableProps {
  selectedOriginIds?: string[];
  allowMultiple?: boolean;
  onSelectedOriginChange: (selectedOriginId: string) => void;
  pageSize?: number;
  allowedCountries?: string[];
}
export const OriginSelectTable = ({
  onSelectedOriginChange,
  allowMultiple,
  selectedOriginIds,
  pageSize,
  allowedCountries,
}: OriginSelectTableProps) => {
  const hydratedOrigins = useStoreState((state) => state.FP_fulfillmentContext.hydratedOrigins);
  const fetchOrigins = useStoreActions((actions) => actions.FP_fulfillmentContext.fetchOrigins);
  const [searchBarText, setSearchBarText] = useState('');
  const classes = useStyles();

  useEffect(() => {
    fetchOrigins();
  }, [fetchOrigins]);

  const onSelectChange = (
    record: PartnerOrigin,
    selected: boolean,
    selectedRows: PartnerOrigin[],
    nativeEvent: Event,
  ) => {
    onSelectedOriginChange(record.originId);
  };

  const columns: ColumnsType<HydratedOrigin> = [
    {
      // TODO Add sorting and filtering for this column
      title: 'Name',
      key: 'name',
      render: (val: HydratedOrigin) => {
        return val.name || '(no name)';
      },
    },
    {
      // TODO Add sorting and filtering for this column
      title: 'Type',
      key: 'type',
      onFilter: (value, origin: HydratedOrigin) => origin.originType === value,
      filters: [
        {
          text: OriginTypeDisplayMap['fc'],
          value: 'fc',
        },
        {
          text: OriginTypeDisplayMap['store'],
          value: 'store',
        },
        {
          text: OriginTypeDisplayMap['at_large'],
          value: 'at_large',
        },
      ],
      render: (val: HydratedOrigin) => {
        return OriginTypeDisplayMap[val.originType as OriginType] || '(no type)';
      },
    },
    {
      title: 'Address',
      key: 'company',
      render: (val: HydratedOrigin) => {
        if (val.address?.validationInfo.configurationValid) {
          return formatAddressAsList(val.address);
        } else {
          return '(no address associated with origin)';
        }
      },
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (val: HydratedOrigin) => {
        if (val.contact?.validationInfo.configurationValid) {
          return formattedContactAsList(val.contact);
        } else {
          return '(no contact associated with origin)';
        }
      },
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (val: HydratedOrigin) => {
        if (val.schedule?.validationInfo.configurationValid) {
          return fomattedScheduleAsList(val.schedule);
        } else {
          return '(no valid schedule associated with origin)';
        }
      },
    },
    {
      title: 'Status',
      key: 'status',
      onFilter: (value, origin: HydratedOrigin) => origin.validationInfo.configurationValid === value,
      filters: [
        {
          text: 'Configuration Valid',
          value: true,
        },
        {
          text: 'Configuration Invalid',
          value: false,
        },
      ],
      render: (val: HydratedOrigin) => {
        return renderHydrationValidationStatus(val.validationInfo);
      },
    },
  ];

  const allData = Object.values(hydratedOrigins)
    .filter(
      (o) => o.name && o.originType && o.active && isCountryAllowed(allowedCountries, o.address?.location?.country),
    )
    .sort(defaultAssetTableSortComparator)
    .map((origin: any) => {
      origin.key = origin.originId;
      return origin;
    }) as any;

  const data = allData.filter((origin: HydratedOrigin) => {
    const regex = new RegExp(escapeRegexChars(searchBarText), 'ig');
    const { name, originId } = origin;
    const {
      address1 = '',
      address2 = '',
      address3 = '',
      address4 = '',
      companyName = '',
      city = '',
      postalCode = '',
      country = '',
      region = '',
    } = origin.address?.location ?? {};
    const originText = [
      name,
      address1,
      address2,
      address3,
      address4,
      companyName,
      city,
      postalCode,
      country,
      region,
    ].join(' ');
    const { contactName = '', name: contactAssetName, email = '', contactId = '' } = origin.contact ?? {};
    const contactText = [contactAssetName, contactName, email].join(' ');
    const { name: scheduleName = '', scheduleId = '' } = origin.schedule ?? {};
    // exact match on ids
    // fuzzy match everything else
    return (
      originId === searchBarText ||
      contactId === searchBarText ||
      origin.address?.addressId === searchBarText ||
      scheduleId === searchBarText ||
      regex.test(originText) ||
      regex.test(origin.name) ||
      regex.test(contactText) ||
      regex.test(scheduleName)
    );
  });

  return (
    <div>
      <div style={{ paddingBottom: '5px' }}>
        <SearchBar setSearchBarValue={setSearchBarText} placeholder={'Search Origins'} />
      </div>
      <Table
        size={'small'}
        rowSelection={{
          ...(selectedOriginIds ? { selectedRowKeys: selectedOriginIds } : {}),
          hideSelectAll: true,
          type: allowMultiple ? 'checkbox' : 'radio',
          onSelect: onSelectChange,
        }}
        columns={columns}
        dataSource={data}
        bordered={false}
        pagination={{ pageSize: pageSize ?? 10, showSizeChanger: false }}
        className={classes.tableStyles}
      />
    </div>
  );
};
